// src/pages/PartnerApply.jsx
import React, { useRef, useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import app from "../firebase";
import { useToast } from "../context/ToastContext";

import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL
} from "firebase/storage";
import {
  FaIdCard,
  FaFileUpload,
  FaCheckCircle,
  FaCheck,
  FaShieldAlt
} from "react-icons/fa";


const db = getFirestore(app);
const storage = getStorage(app);

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6 },
};

const MAX_FILE_BYTES = 25 * 1024 * 1024;

const prettyBytes = (b) => {
  if (!b && b !== 0) return "";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0, n = b;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

function FileUpload({
  name,
  label,
  icon,
  file,
  accept,
  onChange,
  required = false,
}) {
  const inputRef = useRef(null);
  const openPicker = () => inputRef.current?.click();
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openPicker();
    }
  };
  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold">{label}</label>
      <div
        role="button"
        tabIndex={0}
        onClick={openPicker}
        onKeyDown={onKey}
        className="group rounded-2xl border border-gray-200 hover:border-[#002b5c] hover:shadow-md transition p-4 flex items-center gap-4 cursor-pointer"
      >
        <div className="shrink-0 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#e8f1fb] text-[#002b5c]">
          {icon}
        </div>
        <div className="flex-1">
          <div className="text-sm font-medium">
            {file ? file.name : "Click to choose a file"}
          </div>
          <div className="text-xs text-gray-500">
            {file ? prettyBytes(file.size) : "PDF, JPG, or PNG"}
            {required && <span className="ml-2">• Required</span>}
          </div>
        </div>
        <button
          type="button"
          className="px-3 py-2 rounded-xl text-sm font-semibold bg-[#002b5c] text-white group-hover:opacity-90"
        >
          Browse
        </button>
        <input
          ref={inputRef}
          name={name}
          type="file"
          accept={accept}
          onChange={onChange}
          className="hidden"
        />
      </div>
    </div>
  );
}

function LinePills({ value, onToggle }) {
  const items = [
    ["medicare", "Medicare"],
    ["life", "Life"],
    ["health", "ACA/Health"],
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {items.map(([key, label]) => {
        const active = !!value[key];
        return (
          <button
            key={key}
            type="button"
            aria-pressed={active}
            onClick={() => onToggle(key)}
            className={`px-4 py-2 rounded-full border text-sm font-medium transition ${
              active
                ? "bg-[#002b5c] text-white border-[#002b5c]"
                : "bg-white text-gray-700 border-gray-300 hover:border-[#002b5c] hover:text-[#002b5c]"
            } inline-flex items-center gap-2`}
          >
            <span
              className={`inline-flex items-center justify-center rounded-full h-4 w-4 border ${
                active
                  ? "bg-white text-[#002b5c] border-white"
                  : "border-gray-300 text-transparent"
              }`}
            >
              <FaCheck className="h-3 w-3" />
            </span>
            {label}
          </button>
        );
      })}
    </div>
  );
}

const safeSegment = (s) =>
  String(s)
    .normalize("NFKD")
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^_+|_+$/g, "");

const safeFileName = (file) => {
  const name = file?.name || `file_${Date.now()}`;
  const dot = name.lastIndexOf(".");
  const base = dot > -1 ? name.slice(0, dot) : name;
  const ext = dot > -1 ? name.slice(dot) : "";
  return `${safeSegment(base).slice(0, 64)}${safeSegment(ext)}`;
};

const guessContentType = (file) => {
  if (file?.type) return file.type;
  const n = (file?.name || "").toLowerCase();
  if (n.endsWith(".pdf")) return "application/pdf";
  if (n.endsWith(".png")) return "image/png";
  if (n.endsWith(".jpg") || n.endsWith(".jpeg")) return "image/jpeg";
  return "application/octet-stream";
};

// ✅ FIXED: Removed forbidden firebaseStorageDownloadTokens from metadata
const uploadToStorage = async (path, file, storageInstance) => {
  if (!file) throw new Error("No file provided for upload");
  const contentType = guessContentType(file);
  const storageRef = ref(storageInstance, path);

  await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, { contentType });
    task.on("state_changed", null, reject, resolve);
  });

  const url = await getDownloadURL(storageRef);
  return { url, path };
};

export default function PartnerApply() {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    npn: "",
    state: "",
    lines: { medicare: true, life: true, health: false },
    notes: "",
    company: "",
    consent: false,
  });
  const [files, setFiles] = useState({ license: null, eo: null });

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };
  const toggleLine = (key) =>
    setForm((s) => ({ ...s, lines: { ...s.lines, [key]: !s.lines[key] } }));
  const onFile = (e) => {
    const { name, files: f } = e.target;
    setFiles((s) => ({ ...s, [name]: f[0] || null }));
  };

  const validate = () => {
    if (!form.fullName.trim()) return "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "Please enter a valid email.";
    if (!form.phone.trim()) return "Please enter your phone number.";
    if (!form.npn.trim()) return "Please enter your NPN.";
    if (!form.state.trim()) return "Please enter your state.";
    if (!files.license) return "Please upload your License.";
    if (!files.eo) return "Please upload your E&O proof.";
    if (files.license.size > MAX_FILE_BYTES)
      return `License too large (max ${prettyBytes(MAX_FILE_BYTES)})`;
    if (files.eo.size > MAX_FILE_BYTES)
      return `E&O too large (max ${prettyBytes(MAX_FILE_BYTES)})`;
    if (!form.consent) return "Please agree to be contacted.";
    return null;
  };
 const uploadToStorage = async (path, file, storageInstance) => {
  if (!file) throw new Error("No file provided for upload");
  const contentType = guessContentType(file);
  const storageRef = ref(storageInstance, path);

  await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, { contentType });
    task.on("state_changed", null, reject, resolve);
  });

  const url = await getDownloadURL(storageRef);
  return { url, path };
};

const handleSubmit = async (e) => {
  e.preventDefault();
  if (loading) return;

  const errMsg = validate();
  if (errMsg) return addToast(errMsg, "warning");

  try {
    setLoading(true);
    const stamp = Date.now();
    const safePerson = safeSegment(form.fullName).slice(0, 60);
    const base = `partner-applications/${stamp}-${safePerson}`;

    const license = await uploadToStorage(
      `${base}/license_${safeFileName(files.license)}`,
      files.license,
      storage
    );
    const eo = await uploadToStorage(
      `${base}/eo_${safeFileName(files.eo)}`,
      files.eo,
      storage
    );

    await addDoc(collection(db, "partner_applications"), {
      ...form,
      licenseUrl: license.url,
      eoUrl: eo.url,
      createdAt: serverTimestamp(),
      status: "new",
    });

    // Call email API
    await fetch("/api/partner-apply", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...form,
        licenseUrl: license.url,
        eoUrl: eo.url,
      }),
    });

    addToast("Thanks! Your application was submitted.", "success");
    setForm({
      fullName: "",
      email: "",
      phone: "",
      npn: "",
      state: "",
      lines: { medicare: true, life: true, health: false },
      notes: "",
      company: "",
      consent: false,
    });
    setFiles({ license: null, eo: null });
  } catch (error) {
    console.error("Submit failed", error);
    addToast("Something went wrong. Please try again.", "error");
  } finally {
    setLoading(false);
  }
};


  return (
    <>
      <Helmet>
        <title>Partner Application | Your Choice Associates</title>
        <meta
          name="description"
          content="Apply to become a broker partner with Your Choice Associates. Upload license and E&O proof and we’ll follow up quickly."
        />
        <link
          rel="canonical"
          href="https://yourchoiceassociates.org/partner-apply"
        />
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#002b5c] to-[#001b35] text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-16">
          <motion.h1
            {...fade}
            className="text-3xl md:text-5xl font-extrabold leading-tight"
          >
            Partner Application
          </motion.h1>
          <p className="text-white/90 mt-3 max-w-2xl">
            Fill out the form below. Our team will review, confirm appointments,
            and reach out with next steps.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-8">
          <form
            onSubmit={handleSubmit}
            className="grid md:grid-cols-2 gap-6"
            noValidate
          >
            {/* Honeypot */}
            <input
              type="text"
              name="company"
              value={form.company}
              onChange={onChange}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
            />

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold mb-1">
                Full Name
              </label>
              <input
                name="fullName"
                value={form.fullName}
                onChange={onChange}
                className="w-full rounded-2xl border border-gray-300 focus:ring-2 focus:ring-[#99b7e6] focus:border-[#99b7e6] p-3"
                placeholder="Jane Doe"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">Email</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                className="w-full rounded-2xl border border-gray-300 focus:ring-2 focus:ring-[#99b7e6] focus:border-[#99b7e6] p-3"
                placeholder="jane@email.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">Phone</label>
              <input
                name="phone"
                value={form.phone}
                onChange={(e) =>
                  onChange({
                    target: {
                      name: "phone",
                      value: e.target.value.replace(/[^\d()+\-.\s]/g, ""),
                    },
                  })
                }
                className="w-full rounded-2xl border border-gray-300 focus:ring-2 focus:ring-[#99b7e6] focus:border-[#99b7e6] p-3"
                placeholder="(555) 555-5555"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">NPN</label>
              <input
                name="npn"
                value={form.npn}
                onChange={onChange}
                className="w-full rounded-2xl border border-gray-300 focus:ring-2 focus:ring-[#99b7e6] focus:border-[#99b7e6] p-3"
                placeholder="National Producer Number"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">State</label>
              <input
                name="state"
                value={form.state}
                onChange={onChange}
                className="w-full rounded-2xl border border-gray-300 focus:ring-2 focus:ring-[#99b7e6] focus:border-[#99b7e6] p-3"
                placeholder="e.g., FL"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold mb-2">
                Lines of Business
              </label>
              <LinePills value={form.lines} onToggle={toggleLine} />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold mb-1">
                Notes (optional)
              </label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={onChange}
                rows="4"
                className="w-full rounded-2xl border border-gray-300 focus:ring-2 focus:ring-[#99b7e6] focus:border-[#99b7e6] p-3"
                placeholder="Experience, carriers, or questions…"
              />
            </div>

            <div>
              <FileUpload
                name="license"
                label="License (PDF/JPG/PNG)"
                icon={<FaIdCard className="text-lg" />}
                file={files.license}
                accept=".pdf,image/*"
                onChange={onFile}
                required
              />
            </div>

            <div>
              <FileUpload
                name="eo"
                label="E&O Proof (PDF/JPG/PNG)"
                icon={<FaShieldAlt className="text-lg" />}
                file={files.eo}
                accept=".pdf,image/*"
                onChange={onFile}
                required
              />
            </div>

            <div className="md:col-span-2">
              <div className="mt-2 flex items-start gap-3">
                <input
                  id="consent"
                  name="consent"
                  type="checkbox"
                  checked={form.consent}
                  onChange={onChange}
                  className="mt-1 h-4 w-4 rounded border-[#002b5c] text-[#002b5c] focus:ring-[#002b5c]"
                  required
                />
                <label htmlFor="consent" className="text-sm text-gray-700">
     <span>
    I agree to receive email's & SMS notifications from <strong>Your Choice Associates</strong> about my requests, appointments, and account-related alerts. Message frequency varies. Msg&data rates may apply. Reply <strong>STOP</strong> to cancel, <strong>HELP</strong> for help. 
    See our <a href="/privacy" className="underline">Privacy Policy</a> and <a href="/terms" className="underline">Terms</a>.
  </span>
                </label>
              </div>
            </div>
            <div className="md:col-span-2 flex items-center justify-between pt-2">
              <Link
                to="/how-to-HealthInsurance.jsxbecome-a-partner"
                className="text-[#002b5c] font-semibold hover:underline"
              >
                ← Back to How to Become a Partner
              </Link>
              <button
                type="submit"
                disabled={loading}
                className={`inline-flex items-center gap-2 px-5 py-3 rounded-2xl font-semibold transition ${
                  loading
                    ? "bg-gray-300 text-gray-600"
                    : "bg-[#002b5c] text-white hover:opacity-90"
                }`}
              >
                {loading ? (
                  <>
                    <svg
                      className="h-5 w-5 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-90"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>{" "}
                    Submitting…
                  </>
                ) : (
                  <>
                    Submit Application <FaFileUpload />
                  </>
                )}
              </button>
            </div>

            <div className="md:col-span-2 text-sm text-gray-500 flex items-center gap-2">
              <FaCheckCircle /> Your files are stored securely. We’ll never
              share them outside our onboarding team.
            </div>

          </form>
        </div>
      </section>

      <Footer />
    </>
  );
}
